import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './style.module.css';
import Typography from '../../../../components/Typography/Typography';
import { useGetSellerDashboardCardsQuery } from '../../../../redux/services/dashboardApi';
import type { RootState } from '../../../../redux/store';

interface StatItemData {
  id: string;
  label: string;
  value: number | string;
  type:
    | 'completed'
    | 'pending'
    | 'cancelled'
    | 'highStock'
    | 'lowStock'
    | 'outStock'
    | 'totalOrder'
    | 'rating'
    | 'followers';
  indicatorColor: 'black';
  indicatorIcon?: string;
  useIcon?: boolean;
}

interface StatCardData {
  id: string;
  title: string;
  items: StatItemData[];
}

const StatItem: React.FC<{ data: StatItemData }> = ({ data }) => {
  const formatValue = (value: number | string): string =>
    typeof value === 'string' ? value : value.toLocaleString();

  const renderIndicator = () =>
    data.useIcon && data.indicatorIcon ? (
      <div className={styles.statIndicatorIcon}>
        <img
          src={data.indicatorIcon}
          alt={`${data.label} icon`}
          className={styles.indicatorImage}
        />
      </div>
    ) : (
      <div
        className={`${styles.statIndicator} ${styles[data.indicatorColor]}`}
      ></div>
    );

  return (
    <div className={`${styles.statItem} ${styles[data.type]}`}>
      <div className={styles.statItemLeft}>
        {renderIndicator()}
        <Typography
          variant="bodyRegular"
          fontFamily="satoshi"
          weight="medium"
          className={`${styles.statLabel} ${styles[data.type]}`}
        >
          {data.label}
        </Typography>
      </div>
      <Typography
        variant="heading4"
        fontFamily="satoshi"
        weight="bold"
        className={`${styles.statValue} ${styles[data.type]}`}
      >
        {formatValue(data.value)}
      </Typography>
    </div>
  );
};

const StatCard: React.FC<{ data: StatCardData }> = ({ data }) => (
  <div className={styles.statCard}>
    <Typography
      variant="heading3"
      fontFamily="satoshi"
      weight="bold"
      className={styles.statCardTitle}
    >
      {data.title}
    </Typography>
    <div className={styles.statItems}>
      {data.items.map((item) => (
        <StatItem key={item.id} data={item} />
      ))}
    </div>
  </div>
);

const StatCardSkeleton: React.FC = () => (
  <div className={styles.statCard}>
    <div className={styles.skeletonTitle}></div>
    <div className={styles.statItems}>
      {[1, 2, 3].map((i) => (
        <div key={i} className={styles.skeletonItem}>
          <div className={styles.skeletonLeft}>
            <div className={styles.skeletonIcon}></div>
            <div className={styles.skeletonLabel}></div>
          </div>
          <div className={styles.skeletonValue}></div>
        </div>
      ))}
    </div>
  </div>
);

export const DashboardStatCard: React.FC = () => {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('week');
  const selectedStoreId = useSelector((state: RootState) => state.selectedStore.storeId);

  const { data, isLoading, isError, error, refetch } = useGetSellerDashboardCardsQuery({
    period,
    ...(selectedStoreId !== 'all' && { store_id: selectedStoreId }),
  });

  if (isLoading) {
    return (
      <div className={styles.dashboardContent}>
        <div className={styles.periodSwitcher}>
          {(['week', 'month', 'year'] as const).map((option) => (
            <button
              key={option}
              disabled
              className={`${styles.periodButton} ${
                period === option ? styles.active : ''
              }`}
            >
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
        <div className={styles.dashboardContainer}>
          <div className={styles.statsGrid}>
            {[1, 2, 3].map((i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.dashboardContent}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <Typography variant="heading3" fontFamily="satoshi" weight="bold">
            Failed to load dashboard statistics
          </Typography>
          <Typography variant="bodyRegular" fontFamily="satoshi" weight="regular">
            {error && 'status' in error
              ? `Error: ${error.status}`
              : 'Unable to fetch dashboard data. Please try again.'}
          </Typography>
          <button onClick={() => refetch()} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statsData: StatCardData[] = [
    {
      id: 'orders',
      title: 'Orders',
      items: [
        {
          id: 'completed',
          label: 'Completed Orders',
          value: data.orders.completed_orders,
          type: 'completed',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/completedIcon.svg',
          useIcon: true,
        },
        {
          id: 'pending',
          label: 'Pending Orders',
          value: data.orders.pending_orders,
          type: 'pending',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/pendingIcon.svg',
          useIcon: true,
        },
        {
          id: 'cancelled',
          label: 'Cancelled Orders',
          value: data.orders.cancelled_orders,
          type: 'cancelled',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/cancelledIcon.svg',
          useIcon: true,
        },
      ],
    },
    {
      id: 'products',
      title: 'Available Product',
      items: [
        {
          id: 'highStock',
          label: 'High Stock',
          value: data.products_overview.high_stock,
          type: 'highStock',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/highStock.svg',
          useIcon: true,
        },
        {
          id: 'lowStock',
          label: 'Low Stock',
          value: data.products_overview.low_stock,
          type: 'lowStock',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/lowStock.svg',
          useIcon: true,
        },
        {
          id: 'outStock',
          label: 'Out of Stock',
          value: data.products_overview.out_of_stock,
          type: 'outStock',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/outStock.svg',
          useIcon: true,
        },
      ],
    },
    {
      id: 'growth',
      title: 'Store Growth',
      items: [
        {
          id: 'totalOrder',
          label: 'Total Order',
          value: data.store_growth_overview.total_orders,
          type: 'totalOrder',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/totalOrder.svg',
          useIcon: true,
        },
        {
          id: 'rating',
          label: 'Store Rating',
          value: `${data.store_growth_overview.store_rating.toFixed(1)} ⭐`,
          type: 'rating',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/storeRating.svg',
          useIcon: true,
        },
        {
          id: 'followers',
          label: 'Store Follow',
          value: data.store_growth_overview.store_follows,
          type: 'followers',
          indicatorColor: 'black',
          indicatorIcon: 'dashBoardIcons/dashBoardPage/storeFollow.svg',
          useIcon: true,
        },
      ],
    },
  ];

  return (
    <div className={styles.dashboardContent}>
      <div className={styles.periodSwitcher}>
        {(['week', 'month', 'year'] as const).map((option) => (
          <button
            key={option}
            onClick={() => setPeriod(option)}
            className={`${styles.periodButton} ${
              period === option ? styles.active : ''
            }`}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </button>
        ))}
      </div>

      <div className={styles.dashboardContainer}>
        <div className={styles.statsGrid}>
          {statsData.map((statCard) => (
            <StatCard key={statCard.id} data={statCard} />
          ))}
        </div>
      </div>
    </div>
  );
};